import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, ChevronRight, FolderOpen, ScrollText } from 'lucide-react';

import { usePortalContext } from '../../context/PortalContext';
import { auth, db } from '../../shared/api/firebase/config';
import { FIREBASE_CONFIG } from '../../config/Config';
import { FS } from '../../shared/constants/firestorePaths';
import {
  fetchClientProcesses,
  watchClientRecentNotes,
} from '../../services/PortalProcessosRepository.js';
import PortalPageHeader from '../../components/portal/PortalPageHeader';
import { logClientPortalEventOnce } from '../../utils/portalEventLog';
import { checkProcessesIntegrity } from '../../utils/bidirectionalIntegrityGuard';

const appId = FIREBASE_CONFIG.projectId;

const toMillis = (raw) => {
  try {
    if (raw?.toDate) return raw.toDate().getTime();
    const dt = new Date(raw || 0);
    return Number.isNaN(dt.getTime()) ? 0 : dt.getTime();
  } catch {
    return 0;
  }
};

const resolveTitle = (row) => {
  return (
    String(row?.title || '').trim() ||
    String(row?.caseTitle || '').trim() ||
    String(row?.name || '').trim() ||
    String(row?.assunto || '').trim() ||
    'Processo'
  );
};

const resolveCnj = (row) => {
  return (
    String(row?.numeroCNJ || '').trim() ||
    String(row?.cnj || '').trim() ||
    String(row?.processNumber || '').trim() ||
    String(row?.number || '').trim() ||
    ''
  );
};

const resolveStatus = (row) => {
  return (
    String(row?.status || '').trim() ||
    String(row?.currentStatus || '').trim() ||
    String(row?.phase || '').trim() ||
    String(row?.workflow?.status || '').trim() ||
    '—'
  );
};

const resolveCourt = (row) => {
  return (
    String(row?.tribunal || '').trim() ||
    String(row?.court || '').trim() ||
    String(row?.jurisdiction || '').trim() ||
    String(row?.vara || '').trim() ||
    '—'
  );
};

const resolveLatestMovement = (row) => {
  return (
    String(row?.ultimaMovimentacao || '').trim() ||
    String(row?.lastMovement || '').trim() ||
    String(row?.latestMovement || '').trim() ||
    String(row?.summary || '').trim() ||
    ''
  );
};

const formatDateTime = (raw) => {
  const millis = toMillis(raw);
  if (!millis) return '—';
  try {
    return new Date(millis).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return '—';
  }
};

export default function PortalProcessos() {
  const { crmClient } = usePortalContext();
  const [matters, setMatters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const clientId = useMemo(() => {
    return crmClient?.id ? String(crmClient.id) : '';
  }, [crmClient?.id]);

  const emailCandidates = useMemo(() => {
    const out = [];
    const seen = new Set();
    const values = [crmClient?.contact?.email, crmClient?.email, auth?.currentUser?.email];
    values.forEach((raw) => {
      const safe = String(raw || '').trim();
      const key = safe.toLowerCase();
      if (!safe || seen.has(key)) return;
      seen.add(key);
      out.push(safe);
    });
    return out;
  }, [crmClient?.contact?.email, crmClient?.email]);

  const clientName = useMemo(() => {
    return (
      String(crmClient?.fullName || '').trim() ||
      String(crmClient?.qualification?.physical?.socialName || '').trim() ||
      String(crmClient?.qualification?.physical?.name || '').trim() ||
      String(crmClient?.contact?.name || '').trim() ||
      'Cliente'
    );
  }, [crmClient?.fullName, crmClient?.qualification?.physical?.socialName, crmClient?.qualification?.physical?.name, crmClient?.contact?.name]);

  useEffect(() => {
    if (!clientId) return;

    (async () => {
      try {
        await logClientPortalEventOnce({
          clientId,
          type: 'CLIENT_PORTAL_PROCESSES_VIEWED',
          actorName: clientName,
          payload: {
            text: 'Cliente consultou a área de processos no portal.',
          },
          storage: 'session',
        });
      } catch (e) {
        void e;
      }
    })();
  }, [clientId, clientName]);

  const loadMatters = useCallback(async () => {
    if (!clientId && emailCandidates.length === 0) {
      setMatters([]);
      setError('');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const sorted = await fetchClientProcesses(clientId);
      setMatters(sorted);
    } catch (e) {
      setMatters([]);
      setError(String(e?.message || e || 'Falha ao carregar processos'));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadMatters();
    return () => setMatters([]);
  }, [loadMatters]);

  useEffect(() => {
    if (!clientId || loading) return;
    checkProcessesIntegrity({ processes: matters, clientId });
  }, [clientId, matters, loading]);

  const [recentEvents, setRecentEvents] = useState([]);

  useEffect(() => {
    if (!clientId) { setRecentEvents([]); return; }
    let unsub = null;
    try {
      unsub = watchClientRecentNotes(
        clientId,
        (snap) => setRecentEvents(
          snap.docs
            .map((d) => ({ id: d.id, ...(d.data() || {}) }))
            .filter((ev) => !['CLIENT_PORTAL_SESSION_STARTED', 'CLIENT_PORTAL_PROCESSES_VIEWED', 'CLIENT_PORTAL_PRONTUARIO_VIEWED', 'CLIENT_PORTAL_PROFILE_VIEWED', 'CLIENT_PORTAL_FINANCE_VIEWED', 'CLIENT_PORTAL_VAULT_VIEWED'].includes(String(ev?.type || '').trim().toUpperCase()))
        ),
        () => setRecentEvents([])
      );
    } catch { setRecentEvents([]); }
    return () => { try { unsub && unsub(); } catch {} };
  }, [clientId]);

  const resolveEventLabel = (row) => {
    const type = String(row?.type || '').trim().toUpperCase();
    const payload = row?.payload && typeof row.payload === 'object' ? row.payload : {};
    const text = String(payload?.text || payload?.fileName || '').trim();
    const labels = {
      PORTAL_UPLOAD_SUBMITTED: 'Arquivo enviado para análise',
      PORTAL_UPLOAD_APPROVED: 'Arquivo aprovado pelo escritório',
      PORTAL_UPLOAD_REJECTED: 'Arquivo devolvido para reenvio',
      CLIENT_DOCUMENT_VIEWED: 'Documento visualizado',
      DOCUMENT_PUBLISHED: 'Novo documento disponível',
      DOCUMENT_CONSENT_REQUESTED: 'Documento publicado com consentimento',
      CLIENT_CONSENT_ACCEPTED: 'Termos aceitos pelo cliente',
      CLIENT_CONSENT_DECLINED: 'Termos recusados pelo cliente',
      PETITION_CONSENT_ACCEPTED: 'Petição aprovada pelo cliente',
      PETITION_CONSENT_DECLINED: 'Petição recusada pelo cliente',
      DOCUMENT_SIGNATURE_UPLOADED: 'Documento assinado enviado',
      USER_NOTE_ADDED: 'Anotação adicionada pelo escritório',
      CLIENT_PORTAL_SESSION_STARTED: 'Acesso ao portal',
    };
    return text || labels[type] || type.replace(/_/g, ' ').toLowerCase();
  };

  const resolveStatusBadge = (statusRaw) => {
    const s = String(statusRaw || '').trim().toLowerCase();
    if (!s || s === '—') return { label: '—', cls: 'bg-gray-100 text-gray-500 border-gray-200' };
    if (s.includes('ativo') || s.includes('andamento') || s.includes('progress')) return { label: statusRaw, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    if (s.includes('suspens') || s.includes('paus')) return { label: statusRaw, cls: 'bg-amber-50 text-amber-700 border-amber-200' };
    if (s.includes('arquiv') || s.includes('encerrad') || s.includes('extint') || s.includes('baixad')) return { label: statusRaw, cls: 'bg-gray-100 text-gray-600 border-gray-200' };
    if (s.includes('sentenç') || s.includes('julgad') || s.includes('decisão')) return { label: statusRaw, cls: 'bg-blue-50 text-blue-700 border-blue-200' };
    return { label: statusRaw, cls: 'bg-[#C5A059]/10 text-[#7a5a16] border-[#C5A059]/30' };
  };

  const formatEventDate = (raw) => {
    try {
      const d = typeof raw?.toDate === 'function' ? raw.toDate() : new Date(raw);
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return ''; }
  };

  const navigate = useNavigate();

  return (
    <div className="space-y-4">
      <PortalPageHeader
        icon={<Briefcase className="w-5 h-5 text-[#C5A059]" />}
        title="Meu Caso"
        subtitle="Acompanhe os dados e os últimos acontecimentos do seu processo."
        badge="Processos"
      />

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 shadow-sm text-xs text-red-800 whitespace-pre-wrap">{error}</div>
      ) : null}

      {loading ? (
        <div className="border border-gray-200 rounded-lg px-3 py-2 bg-white">
          <div className="text-xs text-gray-500 portal-loading-deferred">Carregando processos...</div>
        </div>
      ) : matters.length === 0 ? (
        <div className="border border-gray-200 rounded-lg px-3 py-3 bg-white">
          <div className="text-xs font-semibold text-[#20243B]">Nenhum processo ativo no momento.</div>
          <div className="text-[11px] text-gray-500 mt-0.5">Se houver um novo andamento, ele aparecerá aqui automaticamente.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {matters.map((row) => {
            const title = resolveTitle(row);
            const cnj = resolveCnj(row);
            const status = resolveStatus(row);
            const court = resolveCourt(row);
            const latestMovement = resolveLatestMovement(row);
            const updatedLabel = formatDateTime(row?.updatedAt || row?.lastUpdatedAt || row?.createdAt);

            return (
              <div key={row.id} className="border border-gray-200 rounded-lg p-3 bg-white">
                <div className="text-xs font-bold text-[#20243B] truncate mb-2" title={title}>{title}</div>

                <div className="space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] text-gray-500 shrink-0">CNJ</span>
                    <span className="text-[11px] font-medium text-gray-700 text-right break-all">{cnj || '—'}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] text-gray-500 shrink-0">Status</span>
                    {(() => { const sb = resolveStatusBadge(status); return (
                      <span className={`px-1.5 py-0 rounded border text-[9px] font-bold ${sb.cls}`}>{sb.label}</span>
                    ); })()}
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] text-gray-500 shrink-0">Tribunal</span>
                    <span className="text-[11px] font-medium text-gray-700 text-right">{court}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] text-gray-500 shrink-0">Atualizado</span>
                    <span className="text-[11px] font-medium text-gray-700 text-right">{updatedLabel}</span>
                  </div>
                  {latestMovement ? (
                    <div className="pt-1.5 border-t border-gray-50">
                      <div className="text-[10px] text-gray-500 mb-0.5">Movimentação</div>
                      <div className="text-[11px] text-gray-700 break-words">{latestMovement}</div>
                    </div>
                  ) : null}
                </div>

                <div className="mt-2 pt-2 border-t border-gray-50 flex flex-wrap gap-1.5">
                  <button type="button" onClick={() => navigate('/portal/documentos')}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-gray-200 bg-gray-50 text-[10px] font-medium text-gray-600 hover:border-[#C5A059] hover:text-[#20243B] transition-colors">
                    <FolderOpen className="w-3 h-3" /> Arquivos
                  </button>
                  <button type="button" onClick={() => navigate('/portal/prontuario')}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-gray-200 bg-gray-50 text-[10px] font-medium text-gray-600 hover:border-[#C5A059] hover:text-[#20243B] transition-colors">
                    <ScrollText className="w-3 h-3" /> Histórico
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* Timeline cronológica — agrupada por dia (padrão GitHub/Linear) */}
      {recentEvents.length > 0 && (
        <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
            <div className="flex items-baseline gap-2">
              <div className="text-xs font-bold text-[#20243B]">Histórico do caso</div>
              <div className="text-[11px] text-gray-500">{recentEvents.length} acontecimento{recentEvents.length === 1 ? '' : 's'}</div>
            </div>
            <button
              type="button"
              onClick={() => navigate('/portal/prontuario')}
              className="inline-flex items-center gap-1 text-[11px] font-bold text-[#C5A059] hover:text-[#20243B] transition-colors"
            >
              Ver tudo <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          <div>
            {(() => {
              // Agrupar eventos por dia (UX padrão activity feed)
              const groups = [];
              let currentKey = null;
              for (const ev of recentEvents) {
                const dateLabel = formatEventDate(ev.createdAt) || '—';
                if (dateLabel !== currentKey) {
                  groups.push({ dateLabel, items: [] });
                  currentKey = dateLabel;
                }
                groups[groups.length - 1].items.push(ev);
              }
              return groups.map((group, gi) => (
                <div key={`${group.dateLabel}-${gi}`}>
                  <div className="sticky top-0 z-[1] px-3 py-1 bg-gray-50/95 backdrop-blur-sm border-b border-gray-100 text-[10px] font-bold uppercase tracking-wide text-gray-500">
                    {group.dateLabel}
                  </div>
                  {group.items.map((ev) => (
                    <div key={ev.id} className="flex items-center gap-2.5 px-3 py-1.5 border-b border-gray-50 last:border-b-0 hover:bg-gray-50 transition-colors">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059] shrink-0" />
                      <div className="flex-1 min-w-0 text-[12px] font-medium text-[#20243B] truncate">{resolveEventLabel(ev)}</div>
                    </div>
                  ))}
                </div>
              ));
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
